import type { FileItem } from '@/components/smartai/api/detectRepository';

export interface CodeAnalysisResult {
  hasTestFiles: boolean;
  totalFiles: number;
  testFilesCount: number;
  nonTestFilesCount: number;
  invalidExtensionCount: number;
  testFiles: string[];
  nonTestFiles: FileItem[];
  nonTestlargerFiles: FileItem[];
  invalidExtensionFiles: string[];
  nonTestFilesExist: boolean;
  codebaseLanguage: string;
  languageConfidence: string;
  languageBreakdown: Record<
    string,
    { count: number; percentage: string | number }
  >;
  isJavaScript: boolean;
  isTypeScript: boolean;
  isMixed: boolean;
  isCSharp: boolean;
  isPython: boolean;
  isJava: boolean;
  isFrontend: boolean;
}

const TEST_PATTERNS = [
  /\.test\.(js|jsx|ts|tsx)$/i,
  /\.spec\.(js|jsx|ts|tsx)$/i,
  // Directory structure — not Python filename prefixes like test_*.py
  /(^|\/)__tests__\//i,
  /(^|\/)tests?\//i,
  /\/spec\//i,
  /\/e2e\//i,
  /\/integration\//i,
  /\.e2e\.(js|jsx|ts|tsx)$/i,
  /\.int\.(js|jsx|ts|tsx)$/i,
  /\.integration\.(js|jsx|ts|tsx)$/i,
  /cypress\//i,
  /playwright\//i,
  /\.stories\.(js|jsx|ts|tsx)$/i,
  /src\/coverage\.controller\.ts/i,
  /(^|\/)coverage-webpack\.config\.(js|jsx|ts|tsx)$/i,
  // C# test patterns
  /Tests?\.(cs)$/i,
  /\.Tests?\//i,
  /\.UnitTests?\//i,
  /\.IntegrationTests?\//i,
  /Spec\.(cs)$/i,
  // Python: config only. Production modules are named test_*.py.
  /(^|\/)conftest\.py$/i,
];

const VALID_EXTENSIONS =
  /\.(js|jsx|ts|tsx|cs|py|java)$|\/src\/coverage\.controller\.ts/i;
const ANGULAR_JSON = /(.*[\\/])?angular\.json/;
const PROJECT_JSON = /(.*[\\/])?project\.json/;
const JS_PATTERN = /\.(js|jsx)$/i;
const TS_PATTERN = /\.(ts|tsx)$/i;
const CS_PATTERN = /\.cs$/i;
const PY_PATTERN = /\.py$/i;
const JAVA_PATTERN = /\.java$/i;
const SIZE_LIMIT = 10 ** 6;

export function filterNonTestFiles(filePaths: FileItem[]): CodeAnalysisResult {
  const testFiles: string[] = [];
  const nonTestFiles: FileItem[] = [];
  const nonTestlargerFiles: FileItem[] = [];
  const invalidExtensionFiles: string[] = [];
  let isFrontend = false;
  let jsFileCount = 0;
  let tsFileCount = 0;
  let csFileCount = 0;
  let pyFileCount = 0;
  let javaFileCount = 0;

  for (const file of filePaths) {
    if (!VALID_EXTENSIONS.test(file.path)) {
      if (ANGULAR_JSON.test(file.path) || PROJECT_JSON.test(file.path))
        isFrontend = true;
      invalidExtensionFiles.push(file.path);
      continue;
    }

    const isTestFile = TEST_PATTERNS.some(p => p.test(file.path));
    if (isTestFile) {
      testFiles.push(file.path);
    } else {
      if (file.size < SIZE_LIMIT) {
        nonTestFiles.push(file);
      } else {
        nonTestlargerFiles.push(file);
      }
      if (JS_PATTERN.test(file.path)) jsFileCount++;
      else if (TS_PATTERN.test(file.path)) tsFileCount++;
      else if (CS_PATTERN.test(file.path)) csFileCount++;
      else if (PY_PATTERN.test(file.path)) pyFileCount++;
      else if (JAVA_PATTERN.test(file.path)) javaFileCount++;
    }
  }

  const total =
    jsFileCount + tsFileCount + csFileCount + pyFileCount + javaFileCount;
  const jsPercent = total > 0 ? (jsFileCount / total) * 100 : 0;
  const tsPercent = total > 0 ? (tsFileCount / total) * 100 : 0;
  const csPercent = total > 0 ? (csFileCount / total) * 100 : 0;
  const pyPercent = total > 0 ? (pyFileCount / total) * 100 : 0;
  const javaPercent = total > 0 ? (javaFileCount / total) * 100 : 0;

  let primaryLanguage = 'unknown';
  let langConfidence = 0;
  const maxPercent = Math.max(
    jsPercent,
    tsPercent,
    csPercent,
    pyPercent,
    javaPercent,
  );

  if (maxPercent === 0) {
    primaryLanguage = 'unknown';
    langConfidence = 0;
  } else if (
    javaPercent === maxPercent &&
    javaPercent > tsPercent &&
    javaPercent > jsPercent &&
    javaPercent > csPercent &&
    javaPercent > pyPercent
  ) {
    primaryLanguage = 'Java';
    langConfidence = javaPercent;
  } else if (
    pyPercent === maxPercent &&
    pyPercent > tsPercent &&
    pyPercent > jsPercent &&
    pyPercent > csPercent &&
    pyPercent > javaPercent
  ) {
    primaryLanguage = 'Python';
    langConfidence = pyPercent;
  } else if (
    csPercent === maxPercent &&
    csPercent > tsPercent &&
    csPercent > jsPercent
  ) {
    primaryLanguage = 'C#';
    langConfidence = csPercent;
  } else if (
    tsPercent === maxPercent &&
    tsPercent > jsPercent &&
    tsPercent > csPercent
  ) {
    primaryLanguage = 'TypeScript';
    langConfidence = tsPercent;
  } else if (
    jsPercent === maxPercent &&
    jsPercent > tsPercent &&
    jsPercent > csPercent
  ) {
    primaryLanguage = 'JavaScript';
    langConfidence = jsPercent;
  } else {
    primaryLanguage = 'Mixed';
    langConfidence = maxPercent;
  }

  return {
    hasTestFiles: testFiles.length > 0,
    totalFiles: filePaths.length,
    testFilesCount: testFiles.length,
    nonTestFilesCount: nonTestFiles.length,
    invalidExtensionCount: invalidExtensionFiles.length,
    testFiles,
    nonTestFiles,
    nonTestlargerFiles,
    invalidExtensionFiles,
    nonTestFilesExist: nonTestlargerFiles.length > 0,
    codebaseLanguage: primaryLanguage,
    languageConfidence: langConfidence.toFixed(2),
    languageBreakdown: {
      javascript: {
        count: jsFileCount,
        percentage: total > 0 ? ((jsFileCount / total) * 100).toFixed(2) : 0,
      },
      typescript: {
        count: tsFileCount,
        percentage: total > 0 ? ((tsFileCount / total) * 100).toFixed(2) : 0,
      },
      csharp: {
        count: csFileCount,
        percentage: total > 0 ? ((csFileCount / total) * 100).toFixed(2) : 0,
      },
      python: {
        count: pyFileCount,
        percentage: total > 0 ? ((pyFileCount / total) * 100).toFixed(2) : 0,
      },
      java: {
        count: javaFileCount,
        percentage: total > 0 ? ((javaFileCount / total) * 100).toFixed(2) : 0,
      },
    },
    isJavaScript: primaryLanguage === 'JavaScript',
    isTypeScript: primaryLanguage === 'TypeScript',
    isMixed: primaryLanguage.includes('Mixed'),
    isCSharp: primaryLanguage === 'C#',
    isPython: primaryLanguage === 'Python',
    isJava: primaryLanguage === 'Java',
    isFrontend,
  };
}
